import {
  ExposureEnum,
  schemaLookupToolConflictPayloadSchema,
  type FieldExposure,
  type SchemaLookupToolConflict,
} from '@reach/shared-types/schema-mappings';
import type { FieldToExpose } from '../lib/api';

type FieldExposureEntry = {
  segments: boolean;
  mergeFields: boolean;
  agentTools: boolean;
  name?: string;
  description?: string;
};

export type FieldExposureState = Record<string, FieldExposureEntry>;

export type SchemaLookupConflictReason = SchemaLookupToolConflict['reason'];

// Normalizes mapping and field-exposure conflicts for the shared conflict UI.
export interface SchemaLookupConflict {
  toolDefinitionId: string;
  toolName?: string;
  schemaDefinitionId?: string;
  fieldName?: string;
  reason?: SchemaLookupConflictReason;
}

export interface ParsedSchemaLookupToolConflict {
  message: string;
  conflicts: SchemaLookupConflict[];
}

/** Parses the structured 409 returned when a mapping change would break active lookup tools. */
export function parseSchemaLookupToolConflict(
  payload: unknown,
): ParsedSchemaLookupToolConflict | undefined {
  const parsed = schemaLookupToolConflictPayloadSchema.safeParse(payload);
  if (!parsed.success) {
    return undefined;
  }

  return {
    message: parsed.data.message,
    conflicts: parsed.data.conflicts.map(conflict => ({
      toolDefinitionId: conflict.toolDefinitionId,
      toolName: conflict.toolName,
      schemaDefinitionId: conflict.schemaDefinitionId,
      reason: conflict.reason,
    })),
  };
}

/** Strips the `$.` or `.` prefix a jsonPath carries to get the schema field name. */
export function jsonPathToFieldName(jsonPath: string): string {
  return jsonPath.replace(/^\$\./, '').replace(/^\./, '');
}

/** Hydrates all schema-wide exposure flags from the API's fieldsToExpose array. */
export function hydrateFieldExposure(fieldsToExpose: FieldToExpose[]): FieldExposureState {
  return Object.fromEntries(
    fieldsToExpose.map(field => {
      const fieldName = jsonPathToFieldName(field.jsonPath);
      return [
        fieldName,
        {
          segments: field.exposure.includes(ExposureEnum.SEGMENTS),
          mergeFields: field.exposure.includes(ExposureEnum.MERGE_FIELDS),
          agentTools: field.exposure.includes(ExposureEnum.AGENT_TOOLS),
          name: field.name,
          description: field.description,
        },
      ];
    }),
  );
}

/** True when a field participates in at least one schema-wide exposure surface. */
export function hasFieldExposure(exposure: FieldExposureEntry | undefined): boolean {
  return Boolean(exposure?.segments || exposure?.mergeFields || exposure?.agentTools);
}

/** Serializes exposure state using the current JSON Schema fields and descriptions. */
export function buildFieldsToExposeFromState(args: {
  fieldExposure: FieldExposureState;
  schemaProperties: Array<{ name: string; description?: string }>;
}): FieldToExpose[] {
  const fieldsToExpose: FieldToExpose[] = [];

  Object.entries(args.fieldExposure).forEach(([fieldName, exposure]) => {
    if (!hasFieldExposure(exposure) || !exposure.name?.trim()) {
      return;
    }

    const field = args.schemaProperties.find(property => property.name === fieldName);
    if (!field) {
      return;
    }

    const exposureTypes: FieldExposure[] = [];
    if (exposure.segments) exposureTypes.push(ExposureEnum.SEGMENTS);
    if (exposure.mergeFields) exposureTypes.push(ExposureEnum.MERGE_FIELDS);
    if (exposure.agentTools) exposureTypes.push(ExposureEnum.AGENT_TOOLS);

    fieldsToExpose.push({
      name: exposure.name,
      description: field.description || exposure.description || '',
      jsonPath: `.${fieldName}`,
      exposure: exposureTypes,
    });
  });

  return fieldsToExpose;
}

export function jsonPathToTopLevelPropertyName(jsonPath: string): string {
  const propertyPath = jsonPathToFieldName(jsonPath);
  const [topLevelProperty] = propertyPath.split('.');
  return topLevelProperty ?? propertyPath;
}

function getSchemaPropertyNames(schema: Record<string, unknown> | undefined): Set<string> {
  const properties = schema?.properties;
  if (!properties || typeof properties !== 'object') {
    return new Set();
  }

  return new Set(Object.keys(properties));
}

export function getRemovedSchemaPropertyNames(args: {
  currentSchema: Record<string, unknown> | undefined;
  nextSchema: Record<string, unknown> | undefined;
}): string[] {
  const currentPropertyNames = getSchemaPropertyNames(args.currentSchema);
  const nextPropertyNames = getSchemaPropertyNames(args.nextSchema);

  return Array.from(currentPropertyNames).filter(propertyName => !nextPropertyNames.has(propertyName));
}

export function removeSchemaField(
  schema: Record<string, unknown>,
  fieldName: string,
): Record<string, unknown> {
  const properties = {
    ...((schema.properties as Record<string, unknown> | undefined) ?? {}),
  };
  delete properties[fieldName];

  const nextSchema: Record<string, unknown> = {
    ...schema,
    properties,
  };

  if (Array.isArray(schema.required)) {
    const requiredFields = schema.required.filter(requiredFieldName => requiredFieldName !== fieldName);
    if (requiredFields.length > 0) {
      nextSchema.required = requiredFields;
    } else {
      delete nextSchema.required;
    }
  }

  return nextSchema;
}

export function removeFieldExposure(
  fieldExposure: FieldExposureState,
  fieldName: string,
): FieldExposureState {
  const nextFieldExposure = { ...fieldExposure };
  delete nextFieldExposure[fieldName];
  return nextFieldExposure;
}

export function removeFieldsToExposeForRemovedProperties<T extends { jsonPath: string }>(args: {
  fieldsToExpose: T[];
  removedPropertyNames: string[];
}): T[] {
  if (args.removedPropertyNames.length === 0) {
    return args.fieldsToExpose;
  }

  const removedPropertyNames = new Set(args.removedPropertyNames);
  return args.fieldsToExpose.filter(field => {
    const topLevelPropertyName = jsonPathToTopLevelPropertyName(field.jsonPath);
    return !removedPropertyNames.has(topLevelPropertyName);
  });
}

export function buildFieldsToExposeUpdate<T extends { jsonPath: string }>(args: {
  currentSchema: Record<string, unknown> | undefined;
  nextSchema: Record<string, unknown> | undefined;
  currentFieldsToExpose: T[];
  nextFieldsToExpose: T[];
}): T[] | undefined {
  const removedSchemaPropertyNames = getRemovedSchemaPropertyNames({
    currentSchema: args.currentSchema,
    nextSchema: args.nextSchema,
  });
  const cleanedFieldsToExpose = removeFieldsToExposeForRemovedProperties({
    fieldsToExpose: args.nextFieldsToExpose,
    removedPropertyNames: removedSchemaPropertyNames,
  });

  const schemaExposesFields = cleanedFieldsToExpose.length > 0;
  const removalNeedsPersisting = removedSchemaPropertyNames.length > 0;
  const exposureNeedsClearing = args.currentFieldsToExpose.length > 0;
  const updateChangesStoredExposure =
    schemaExposesFields || removalNeedsPersisting || exposureNeedsClearing;

  return updateChangesStoredExposure ? cleanedFieldsToExpose : undefined;
}

const objectValue = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

// Extracts lookup tools blocking removal of a field exposed to active tools.
export function schemaExposureLookupConflicts(payload: unknown): SchemaLookupConflict[] {
  const conflicts = objectValue(payload)?.fieldsToExposeConflicts;
  if (!Array.isArray(conflicts)) {
    return [];
  }

  return conflicts.flatMap(value => {
    const conflict = objectValue(value);
    const fieldName = conflict?.fieldName;
    if (
      !conflict ||
      conflict.usageType !== ExposureEnum.AGENT_TOOLS ||
      typeof fieldName !== 'string' ||
      !Array.isArray(conflict.toolDefinitionIds)
    ) {
      return [];
    }
    return conflict.toolDefinitionIds.flatMap(toolDefinitionId =>
      typeof toolDefinitionId === 'string' ? [{ toolDefinitionId, fieldName }] : [],
    );
  });
}
